/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneLocationSheets.tsx
 * FILE_ID: JOBHUB-QA-PHONE-LOCATION-SHEETS-001
 * PURPOSE: Header Location sheet and the Run bar's copy of that sheet.
 */
import type { ContextMenuItem } from '@quotech/ui'
import type { JobQaTabController } from '../useJobQaTabController'
import type { QaPhoneUi } from './useQaPhoneUi'
import { addLocation } from './addPhoneLocation'
import { QaPhoneScopeSheet, type ScopeRow } from './sheets/QaPhoneScopeSheet'

type Rename = { scope: 'areas' | 'checklists'; id: string } | null

export function QaPhoneLocationSheets({
  controller,
  ui,
  areaRows,
  levelRows,
  areaIds,
  barAreaOpen,
  shownBarAreaId,
  onCloseBarArea,
  onPickBarArea,
  rename,
  setRename,
  canBuild,
  itemsForArea,
  openLevel,
}: {
  controller: JobQaTabController
  ui: QaPhoneUi
  areaRows: ScopeRow[]
  levelRows?: { id: string; name: string; empty: boolean }[]
  areaIds: string[]
  barAreaOpen: boolean
  shownBarAreaId: string | null
  onCloseBarArea?: () => void
  onPickBarArea?: (id: string) => void
  rename: Rename
  setRename: (value: Rename) => void
  canBuild: boolean
  itemsForArea: (row: { id: string; area_name: string }) => ContextMenuItem[]
  openLevel: (level: { id: string; floor_name: string }, point: { clientX: number; clientY: number }) => void
}) {
  const { api, selection, workspace } = controller
  const closeBar = onCloseBarArea ?? (() => {})
  const afterMutate = () => {
    workspace.invalidateExecution()
    ui.closeSheet()
  }
  const shared = {
    title: 'Location',
    rows: areaRows,
    selecting: ui.selecting,
    selectedIds: new Set(selection.visibleOf('areas', areaIds)),
    onToggleSelectMode: () => ui.setSelecting((on) => !on),
    onToggle: (id: string) => selection.toggle('areas', id),
    addLabel: 'Add Location',
    onRename: canBuild ? (id: string, name: string) => api.renameArea(id, name).then(afterMutate) : undefined,
    menuFor: canBuild ? (rowId: string) => {
      const row = areaRows.find((item) => item.id === rowId)
      return row ? itemsForArea({ id: row.id, area_name: row.name }) : []
    } : undefined,
    renameId: rename?.scope === 'areas' ? rename.id : null,
    onRenameStarted: () => setRename(null),
    levelRows,
    onOpenLevel: canBuild ? (id: string, point: { clientX: number; clientY: number }) => {
      const level = levelRows?.find((row) => row.id === id)
      if (level) openLevel({ id: level.id, floor_name: level.name }, point)
    } : undefined,
    showClose: true,
  }
  return (
    <>
      <QaPhoneScopeSheet
        {...shared}
        open={ui.openSheet === 'scope-area'}
        onClose={ui.closeSheet}
        currentId={workspace.activeAreaId}
        onPick={(id) => { workspace.selectArea(id); ui.closeSheet() }}
        // Reused from: packages/job-hub/src/jobs/tabs/job-qa/addBlankArea.ts (addBlankArea)
        onAdd={canBuild ? () => addLocation(controller, ui.closeSheet) : undefined}
      />
      <QaPhoneScopeSheet
        {...shared}
        open={barAreaOpen}
        onClose={closeBar}
        currentId={shownBarAreaId}
        onPick={onPickBarArea ?? (() => {})}
        onAdd={canBuild ? () => addLocation(controller, closeBar) : undefined}
      />
    </>
  )
}
